import { UploadOutlined } from "@ant-design/icons"
import { Button, Flex, Tabs, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { SearchBar } from "../../components/Input/SearchBar.tsx"
import { FixedContainer } from "../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { GlobalFilesAllFilesTab } from "./components/GlobalFilesAllFilesTab.tsx"
import { GlobalFilesBusinessDocumentsTab } from "./components/GlobalFilesBusinessDocumentsTab.tsx"
import { GlobalFilesCompanyFilesTab } from "./components/GlobalFilesCompanyFilesTab.tsx"
import { GlobalFilesOverviewTab } from "./components/GlobalFilesOverviewTab.tsx"
import { GlobalFilesPhotosTab } from "./components/GlobalFilesPhotosTab.tsx"
import { GlobalFilesTrashTab } from "./components/GlobalFilesTrashTab.tsx"
import { GlobalUploadModal } from "./components/GlobalUploadModal.tsx"

const { Title, Text } = Typography

type GlobalFilesTab =
  | "overview"
  | "all-files"
  | "company"
  | "business-documents"
  | "photos"
  | "trash"

const isGlobalFilesTab = (value: string | null): value is GlobalFilesTab =>
  value === "overview" ||
  value === "all-files" ||
  value === "company" ||
  value === "business-documents" ||
  value === "photos" ||
  value === "trash"

export default function GlobalFilesPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: GlobalFilesTab = isGlobalFilesTab(searchParams.get("tab"))
    ? (searchParams.get("tab") as GlobalFilesTab)
    : "overview"
  const [search, setSearch] = useState("")
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  // Every tab fetches on mount with no externally-triggered refresh of its
  // own — remounting via this key is what makes "the new file should appear
  // immediately" true after a Global Upload, without reworking four tabs'
  // internal data hooks.
  const [refreshToken, setRefreshToken] = useState(0)

  generateDocumentTitle(t("globalFiles.title", "Files"))

  const goToTab = (
    tab: GlobalFilesTab,
    extraParams?: Record<string, string>
  ) => {
    const next = new URLSearchParams()
    next.set("tab", tab)
    Object.entries(extraParams ?? {}).forEach(([key, value]) =>
      next.set(key, value)
    )
    setSearchParams(next)
  }

  return (
    <FixedContainer>
      <Flex vertical gap="middle" className="size-full">
        <Flex
          align="flex-start"
          justify="space-between"
          gap="middle"
          wrap="wrap"
        >
          <Flex vertical gap={2}>
            <Title level={4} className="!mb-0">
              {t("globalFiles.title", "Files")}
            </Title>
            <Text type="secondary">
              {t(
                "globalFiles.subtitle",
                "All company documents, photos and files in one place."
              )}
            </Text>
          </Flex>
          <Flex gap="small" align="center" wrap="wrap">
            <SearchBar
              placeholder={t(
                "globalFiles.searchPlaceholder",
                "Search files, projects, customers..."
              )}
              onSearch={(value) => setSearch((value ?? "").trim())}
              className="w-[280px]"
            />
            <Button
              icon={<UploadOutlined />}
              onClick={() => setUploadModalOpen(true)}
            >
              {t("globalFiles.upload", "Upload")}
            </Button>
          </Flex>
        </Flex>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => goToTab(key as GlobalFilesTab)}
          items={[
            {
              key: "overview",
              label: t("globalFiles.tabs.overview", "Overview"),
              children: (
                <GlobalFilesOverviewTab
                  key={refreshToken}
                  onNavigateToTab={(tab, extra) => goToTab(tab, extra)}
                />
              )
            },
            {
              key: "all-files",
              label: t("globalFiles.tabs.allFiles", "All Files"),
              children: (
                <GlobalFilesAllFilesTab
                  key={refreshToken}
                  search={search}
                  initialFilter={searchParams.get("filter")}
                />
              )
            },
            {
              key: "company",
              label: t("globalFiles.tabs.company", "Company Files"),
              children: <GlobalFilesCompanyFilesTab key={refreshToken} />
            },
            {
              key: "business-documents",
              label: t(
                "globalFiles.tabs.businessDocuments",
                "Business Documents"
              ),
              children: (
                <GlobalFilesBusinessDocumentsTab
                  key={refreshToken}
                  search={search}
                  initialFilter={searchParams.get("filter")}
                />
              )
            },
            {
              key: "photos",
              label: t("globalFiles.tabs.photosMedia", "Photos & Media"),
              children: (
                <GlobalFilesPhotosTab key={refreshToken} search={search} />
              )
            },
            {
              key: "trash",
              label: t("globalFiles.tabs.trash", "Trash"),
              children: (
                <GlobalFilesTrashTab key={refreshToken} search={search} />
              )
            }
          ]}
        />
      </Flex>

      {uploadModalOpen && (
        <GlobalUploadModal
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => setRefreshToken((n) => n + 1)}
        />
      )}
    </FixedContainer>
  )
}
